import { z } from "zod";
import { MEDICAL_ESCALATION_STANDARD_REPLY } from "./medical-question";
import {
  supportContactCategorySchema,
  supportContactTopicSchema,
} from "./support-contact";

// CAR-17 — patient ↔ doctor chat. The same DTO crosses both client
// boundaries (patient mobile app + clinician admin portal), so the
// canonical shape lives here. CAR-67 hoisted it from the two
// hand-rolled copies that had silently drifted (admin used a
// non-optional `senderId`; mobile allowed null).

// v2.4 reflection contract — `reflection` is a patient-shared reflection copy,
// labelled "Reflektion" in the bubble and carrying its source in `payload`.
// Server-issued only: it is produced by the share endpoint from a stored
// PatientReflection, never accepted on the ordinary send path, so a client
// can't forge one. Additive value — no DB migration, `Message.kind` is a free
// string column (same reasoning as CAR-121's `coach` senderType below).
export const chatMessageKindSchema = z.enum([
  "text",
  "quick_action_request",
  "system_event",
  "reflection",
]);
export type ChatMessageKind = z.infer<typeof chatMessageKindSchema>;

// CAR-121 — `coach` is the automated one-way coach persona (a generic,
// tenant-configured display name, never a DB actor). It posts journey-state
// coach_message bursts into the patient's conversation; live human coach
// replies are explicitly out of scope. Additive value — no DB migration since
// `Message.senderType` is a free string column.
//
// TES-1079 — `support` is "Ella Support": system-sent, one-way notices in the
// patient's support thread (the first is the payment notice after a positive
// treatment decision). Like `coach`, never a DB actor: senderId is null, and
// no staff role can send as it.
export const chatSenderTypeSchema = z.enum([
  "patient",
  "doctor",
  "system",
  "coach",
  "support",
]);
export type ChatSenderType = z.infer<typeof chatSenderTypeSchema>;

// Quick-action payload — present on `quick_action_request` messages, null
// for plain text. The schema is permissive (`params: Record<string,
// unknown>`) because action payloads vary per action key.
export const chatMessagePayloadSchema = z.object({
  action: z.string().optional(),
  params: z.record(z.unknown()).optional(),
  ack: z.boolean().optional(),
  // `reflection` messages: where the shared reflection came from. NB the
  // payload column is NOT encrypted, so this carries the source article KEY
  // and a date — never the article's free-text title and never the answer,
  // which lives in the encrypted body. Same discipline as `coachKey`.
  reflectionId: z.string().optional(),
  sourceArticleKey: z.string().optional(),
  sourceDate: z.string().optional(),
  // TES-1131 — a patient's Support message sent through "Kontakta Ella":
  // the category she chose. A key, never free text (the column is not
  // encrypted).
  supportCategory: supportContactCategorySchema.optional(),
  // TES-1549 — the topic Hem sent her with (support-contact.ts), on a
  // medical question only.
  supportTopic: supportContactTopicSchema.optional(),
});
export type ChatMessagePayload = z.infer<typeof chatMessagePayloadSchema>;

export const chatMessageSchema = z.object({
  id: z.string().uuid(),
  senderType: chatSenderTypeSchema,
  senderId: z.string().nullable(),
  kind: chatMessageKindSchema,
  body: z.string(),
  payload: chatMessagePayloadSchema.nullable(),
  createdAt: z.string(),
  // TES-750 — non-null once the sender edited it. The client renders the
  // "Redigerat" marking off this, so it is a timestamp rather than a boolean:
  // "when" is worth more than "whether" when two people are reading the same
  // thread.
  editedAt: z.string().nullable(),
  // TES-750 — non-null once the sender removed it. `body` is then already the
  // neutral replacement text; the server never ships the original to a client
  // that should not see it.
  deletedAt: z.string().nullable(),
  // TES-1277 — on a human coach's message (senderType 'coach', senderId set)
  // in the patient's reads: that coach's first name, formatted. After a coach
  // change each earlier message still names the coach who wrote it. Absent on
  // every other message, including the automated coach persona's (senderId
  // null). Optional, so older builds ignore it.
  // TES-719 — the same on a doctor's message (senderType 'doctor', senderId
  // set): the doctor who wrote it, not the tenant's doctor persona. Sent on
  // the staff thread read too (employee-api .../:channel/messages), so the
  // admin labels each message with its own author.
  senderDisplayName: z.string().optional(),
  // TES-719 — on the same messages: the media key of the author's portrait,
  // resolved by the server (their own once uploaded, else the tenant's role
  // slot; null = neither, show the silhouette). Absent on unauthored
  // messages and from an older API; clients then use the role slot.
  senderAvatarMediaKey: z.string().nullable().optional(),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

// TES-750 — what a deleted message reads as. In contracts because the app, the
// admin and the API all have to agree on it: if the server sends this string
// and a client also substitutes its own, a future copy change silently only
// half-lands.
export const DELETED_CHAT_MESSAGE_BODY = "Meddelandet har tagits bort";

// TES-758 (Ö-171 ✅ b) — the sender edits their own text. The schema was
// already here, unused, from the first round; now it has a route.
//
// Only `body`. Everything else on a message is provenance and is still locked
// by the database trigger: a message never changes sender or conversation, no
// matter how many times the text is edited.
export const editChatMessageSchema = z.object({
  body: z.string().trim().min(1).max(4000),
});
export type EditChatMessageRequest = z.infer<typeof editChatMessageSchema>;

// TES-649 — the send body, parsed at the boundary. Structural only: an empty
// text, a kind the sender may not use and an unknown quick action are refused
// by the controllers with their own error codes, after this parse. No length
// cap — the app's composer has none, and a long message refused here would be
// a message the patient loses.
export const sendChatMessageSchema = z.object({
  body: z.string(),
  kind: chatMessageKindSchema.optional(),
  payload: z
    .object({
      action: z.string().optional(),
      params: z.record(z.unknown()).optional(),
    })
    .nullable()
    .optional(),
  // TES-1131 — "Kontakta Ella" (app 2.0.6): the category she chose. Support
  // thread only, and optional: 2.0.4 and 2.0.5 never send it.
  category: supportContactCategorySchema.optional(),
  // TES-1549 (Ö-625) — "Jag vill återuppta behandlingen": the request to
  // restart, sent as a medical question. Only with `category`
  // "medical_question"; 2.0.x never sends it.
  topic: supportContactTopicSchema.optional(),
});
export type SendChatMessageRequest = z.infer<typeof sendChatMessageSchema>;

// Staff never send a payload (quick actions are the patient's).
export const staffSendChatMessageSchema = sendChatMessageSchema.pick({
  body: true,
  kind: true,
});
export type StaffSendChatMessageRequest = z.infer<
  typeof staffSendChatMessageSchema
>;

// Chat split — the patient talks to two counterparts in two separate
// conversations. 'doctor' is the medical thread (quick-actions, system acks);
// 'coach' is the coaching thread (automated CAR-121 bursts + human coach-role
// staff). The channel-less patient endpoints stay as a legacy alias for old
// builds (merged read, doctor-directed write).
//
// TES-1079 — 'support' is the Ella Support thread. It is served only to
// clients that declare CLIENT_CONTRACT_CHANGES.supportThread, because a build
// that predates it would render a third thread under the doctor's name and
// could never clear its unread count.
//
// TES-1102 (Simon 2026-10-04) — two-way from signup: the patient writes in it,
// and staff with the admin role (base role or the TES-1089 grant) answer from
// the admin's Support inbox, as Ella Support (senderType 'support' with the
// employee's id as senderId; the automated notices keep senderId null). It is
// never the coach chat: no doctor or coach writes in it through the clinician
// chat routes.
export const CHAT_CHANNELS = ["doctor", "coach", "support"] as const;
export const chatChannelSchema = z.enum(CHAT_CHANNELS);
export type ChatChannel = z.infer<typeof chatChannelSchema>;

// The channels a doctor or coach writes in through the clinician chat routes,
// each locked to her own role's channel on top of this. The patient writes in
// every channel; the support thread's staff side is the admin's Support inbox
// (TES-1102), not those routes.
export const WRITABLE_CHAT_CHANNELS = ["doctor", "coach"] as const;
export type WritableChatChannel = (typeof WRITABLE_CHAT_CHANNELS)[number];
export function isWritableChatChannel(
  channel: ChatChannel,
): channel is WritableChatChannel {
  return (WRITABLE_CHAT_CHANNELS as readonly string[]).includes(channel);
}

// TES-1079 (TES-1069 point 7) — Emelie's support message after a positive
// treatment decision, verbatim. No link and no in-app payment destination:
// the link goes by e-mail only (point 8). Ö-319 (Emelie 2026-10-04,
// TES-1098) replaced the text with her new standard one. Messages already
// posted keep the text they were sent with.
//
// TES-1150 (Emelie 2026-10-07) — Ö-319's last sentence, "När betalningen är
// registrerad kan läkaren färdigställa ditt recept.", described the order she
// has now reversed: the doctor finishes the prescription BEFORE the
// subscription. The message now uses the two sentences of her new
// "Aktivera abonnemanget" step, verbatim, with the address left unnamed
// (a chat message is kept; a masked address in it would outlive a change).
export const TREATMENT_DECISION_SUPPORT_MESSAGE =
  "Du är godkänd för behandling hos Ella. För att behandlingen ska kunna starta behöver ditt abonnemang aktiveras. Information om hur du gör har skickats till din e-postadress.";

// TES-1110 — the standard reply Ella Support may post when staff escalate a
// medical question to the care team. TES-1119: Ö-333 is answered, and the
// text is MEDICAL_ESCALATION_STANDARD_REPLY (medical-question.ts), shared
// with the coach's "Skicka till läkare".
export const SUPPORT_REDIRECT_REPLY_MESSAGE: string | null =
  MEDICAL_ESCALATION_STANDARD_REPLY;

// TES-1277 (TES-1266, Emelie 2026-10-12) — the patient's relationship with a
// personal coach, derived on every read, never stored:
//   - active: her plan gives coach access AND she has a current primary coach
//     (the latest assignment, coach not deactivated, or the single-coach
//     rule). The coach thread is writable.
//   - ended: not active, and the coach thread has at least one message. The
//     history stays readable; her writes are refused (coach_chat_closed).
//   - none: not active, and no coach history.
// `coach` names the current coach while active, null otherwise. Names are
// formatted (formatPersonName): never "EMELIE".
export const COACH_RELATIONSHIP_STATUSES = ["active", "ended", "none"] as const;
export const coachRelationshipStatusSchema = z.enum(
  COACH_RELATIONSHIP_STATUSES,
);
export type CoachRelationshipStatus = z.infer<
  typeof coachRelationshipStatusSchema
>;

export const coachRelationshipSchema = z.object({
  status: coachRelationshipStatusSchema,
  coach: z
    .object({
      firstName: z.string(),
      fullName: z.string(),
    })
    .nullable(),
});
export type CoachRelationship = z.infer<typeof coachRelationshipSchema>;

// One row in the patient's conversation list (the messenger view).
export const chatThreadSchema = z.object({
  channel: chatChannelSchema,
  lastMessage: chatMessageSchema.nullable(),
  lastMessageAt: z.string().nullable(),
  unreadCount: z.number().int().nonnegative(),
});
export type ChatThread = z.infer<typeof chatThreadSchema>;

export const chatThreadsResponseSchema = z.object({
  threads: z.array(chatThreadSchema),
  // TES-1277 — additive; builds before 2.0.9 ignore it.
  coachRelationship: coachRelationshipSchema.optional(),
});
export type ChatThreadsResponse = z.infer<typeof chatThreadsResponseSchema>;

// The lightweight badge poll — tab bar + app icon read this.
//
// TES-1079 — `support` is always reported; `total` counts it only for a
// client that renders the support thread (the contract header). An older
// build sets the icon badge from `total` and has no way to read the support
// thread, so counting it there would leave a badge it can never clear.
export const chatUnreadSchema = z.object({
  total: z.number().int().nonnegative(),
  doctor: z.number().int().nonnegative(),
  coach: z.number().int().nonnegative(),
  support: z.number().int().nonnegative(),
  // TES-1277 — on the patient's poll only (every build polls it on foreground
  // and every 60 s, so a status change reaches the app without a restart).
  // Additive; builds before 2.0.9 ignore it. Not named `coach`: that key is
  // the coach thread's unread count.
  coachRelationship: coachRelationshipSchema.optional(),
});
export type ChatUnread = z.infer<typeof chatUnreadSchema>;

// TES-1102 — the admin's Support inbox: one row per patient whose Ella
// Support thread has a message. No message text: the list is read without a
// support access reason, so it carries who and when, never what. The thread
// itself is read under the reason (CareRelationshipGuard) and logged.
export const supportInboxRowSchema = z.object({
  patientId: z.string(),
  patientName: z.string(),
  lastMessageAt: z.string(),
  // Who spoke last: the patient, or Ella Support (a person or a notice).
  lastSenderType: chatSenderTypeSchema,
  // The patient's messages no staff member has opened.
  unreadCount: z.number().int().nonnegative(),
  // The oldest patient message since Ella Support last spoke; null when
  // nothing is waiting on us.
  waitingSince: z.string().nullable(),
});
export type SupportInboxRow = z.infer<typeof supportInboxRowSchema>;

export const supportInboxResponseSchema = z.object({
  threads: z.array(supportInboxRowSchema),
});
export type SupportInboxResponse = z.infer<typeof supportInboxResponseSchema>;

// The sidebar badge: unread patient messages across every support thread.
export const supportInboxUnreadSchema = z.object({
  unread: z.number().int().nonnegative(),
});
export type SupportInboxUnread = z.infer<typeof supportInboxUnreadSchema>;

// One support thread as staff read it. `staffNames` names the employee behind
// each staff reply by senderId (the patient only ever sees "Ella Support").
export const supportThreadResponseSchema = z.object({
  patient: z.object({ id: z.string(), name: z.string() }),
  messages: z.array(chatMessageSchema),
  staffNames: z.record(z.string()),
});
export type SupportThreadResponse = z.infer<typeof supportThreadResponseSchema>;

// TES-889 (Ö-216), Emelie 2026-09-23: an unanswered patient message becomes
// "försenad" after 24 staffed weekday hours. The same threshold the queues
// use — one definition of late across every surface a clinician reads.
//
// It is a tool for the team to see whether it is keeping its OWN commitment
// ("helgfria vardagar 08–17, vanligtvis svar inom 24 timmar"), not a promise
// to the patient. Nothing here reaches a patient surface.
export const CHAT_DELAYED_STAFFED_HOURS = 24;
